import { evaluatePolicy, type PolicyEvaluationResult, type PolicyRow } from "../policies/policy-engine.service";
import type { ActionType, PermissionAudience, PermissionInput } from "./types";
import {
  CAPABILITY_BY_COMMAND,
  DISPATCH_LIKE_COMMANDS,
  evaluateNonBypassableGuards,
  type GuardFacts,
  type GuardResult,
} from "./command-guards";
import { clampAutonomyScores } from "./score-clamp";

/** Database-derived guard facts (everything the pure guard needs except the
 * command descriptor, which the registry fills in from the definition). */
export type GuardFactsInput = Omit<GuardFacts, "command">;

/**
 * P0' — server-derived autonomy floor by command risk class.
 *
 * `riskClass` is defined by Haulbase per command (it is NOT model-supplied), so
 * it is a trustworthy server signal. Clamping the caller's scores against this
 * floor (`clampAutonomyScores`: risk = max, confidence = min) means a caller —
 * including a model — can never make a high/critical command look safer than its
 * class allows. The clamp therefore bites on EVERY command at this choke point,
 * not only loads that happen to have a stored risk assessment.
 *
 *   low/medium  → permissive (no effect): gated by canAutoExecute + policy.
 *   high        → cannot clear the auto-execute bound (risk ≥ 60 / conf < 60) → approval.
 *   critical    → trips the emergency-lock bound (risk ≥ 80 / conf < 35) → approval.
 */
export const SERVER_AUTONOMY_FLOOR: Record<
  TmsCommandDefinition["riskClass"],
  { riskScore: number; confidenceScore: number }
> = {
  low: { riskScore: 0, confidenceScore: 100 },
  medium: { riskScore: 0, confidenceScore: 100 },
  high: { riskScore: 60, confidenceScore: 59 },
  critical: { riskScore: 80, confidenceScore: 34 },
};

/**
 * P0' — fail closed when guard facts are absent, for every command.
 *
 * Guards are pre-fetched by the Convex layer, which always supplies them on the
 * production path. Absent guard facts mean the non-bypassable checks (tenant
 * status, DNU, authority, compliance, etc.) could not be verified at all — that
 * is an "unknown", not a green light, and an unknown must never resolve to
 * `allow` (docs/agent-harness-plan.md P0'). Every command therefore fails
 * closed to AT LEAST `approval_required`; external-commit / dispatch-like
 * commands (`DISPATCH_LIKE_COMMANDS`) and financial-capability commands fail
 * all the way to `block`, since a human cannot retroactively approve a
 * carrier-facing or money-moving action whose safety checks never ran.
 */
function missingGuardFactsResult(definition: TmsCommandDefinition): GuardResult {
  const highStakes =
    DISPATCH_LIKE_COMMANDS.has(definition.commandName) ||
    CAPABILITY_BY_COMMAND[definition.commandName] === "financial";
  if (highStakes) {
    return {
      decision: "block",
      blocks: [
        {
          code: "guard_facts_unavailable",
          decision: "block",
          reason: "guard facts unavailable — non-bypassable checks cannot be verified for a dispatch/financial command",
        },
      ],
    };
  }
  return {
    decision: "approval_required",
    blocks: [
      {
        code: "guard_facts_unavailable",
        decision: "approval_required",
        reason: "guard facts unavailable — non-bypassable checks cannot be verified; failing closed to approval",
      },
    ],
  };
}

export type TmsCommandName =
  | "create_load"
  | "update_load"
  | "assign_carrier"
  | "tender_carrier"
  | "send_carrier_message"
  | "send_customer_message"
  | "update_load_status"
  | "request_tracking"
  | "request_pod"
  | "attach_document"
  | "place_payment_hold"
  | "release_payment_hold"
  | "create_invoice"
  | "create_settlement";

export type TmsCommandStatus = "executed" | "approval_required" | "blocked" | "failed";

export type TmsCommandDefinition = {
  commandName: TmsCommandName;
  actionType: ActionType;
  audience: PermissionAudience;
  riskClass: "low" | "medium" | "high" | "critical";
  canAutoExecute: boolean;
  canBypass: boolean;
  requiresIdempotencyKey: boolean;
  requiredPayloadFields: string[];
  nonBypassableChecks: string[];
};

export type TmsCommandInput = {
  tenantId: string;
  commandName: TmsCommandName;
  idempotencyKey: string;
  loadId?: string;
  carrierId?: string;
  customerId?: string;
  payload: Record<string, unknown>;
  riskScore: number;
  confidenceScore: number;
  financialExposureCents?: number;
  customerSensitive?: boolean;
  approvalOverride?: boolean;
  policy?: PolicyRow | null;
  extraPolicyFacts?: Pick<
    PermissionInput,
    "marginPercent" | "cargoValueCents" | "carrierAuthorityAgeDays" | "eldConnected" | "carrierPreferred"
  >;
  /** Pre-fetched non-bypassable guard facts. When present, guards are enforced
   * here at the registry choke point before policy and before any provider call.
   * When absent (e.g. demo/unit harnesses), guards are skipped. */
  guardFacts?: GuardFactsInput;
};

export type TmsCommandProviderResult = {
  provider: string;
  externalId: string;
  status: "accepted" | "completed" | "queued";
  rawProviderResponse: Record<string, unknown>;
  occurredAt: string;
};

export type TmsCommandExecutionResult = {
  commandName: TmsCommandName;
  actionType: ActionType;
  audience: PermissionAudience;
  status: TmsCommandStatus;
  permission: PolicyEvaluationResult;
  providerResult?: TmsCommandProviderResult;
  error?: string;
};

export interface TmsCommandAdapter {
  readonly provider: string;
  executeCommand(input: TmsCommandInput, definition: TmsCommandDefinition): Promise<TmsCommandProviderResult>;
}

const NON_BYPASSABLE_COMMON = [
  "tenant_paused_or_suspended",
  "tenant_load_mismatch",
  "malformed_command_input",
  "duplicate_completed_idempotency_key",
  "missing_provider_credentials",
  "disabled_provider_or_workflow",
  "unsupported_provider_capability",
  "emergency_lock",
];

export const TMS_COMMAND_DEFINITIONS: Record<TmsCommandName, TmsCommandDefinition> = {
  create_load: command("create_load", "create_load", "internal", "high", false, true, ["reference"]),
  update_load: command("update_load", "update_load_freight", "internal", "medium", false, true, ["loadId"]),
  assign_carrier: command("assign_carrier", "book_carrier", "carrier", "high", false, true, ["carrierId"]),
  tender_carrier: command("tender_carrier", "tender_carrier", "carrier", "high", false, true, ["carrierId"]),
  send_carrier_message: command("send_carrier_message", "request_eta", "carrier", "low", true, true, ["message"]),
  send_customer_message: command("send_customer_message", "send_customer_update", "customer", "high", false, true, ["message"]),
  update_load_status: command("update_load_status", "update_load_freight", "internal", "high", false, true, ["status"]),
  request_tracking: command("request_tracking", "request_tracking", "carrier", "low", true, true, ["message"]),
  request_pod: command("request_pod", "request_pod", "carrier", "low", true, true, ["message"]),
  attach_document: command("attach_document", "attach_lumper_receipt", "internal", "medium", true, true, ["documentId"]),
  place_payment_hold: command("place_payment_hold", "place_carrier_payment_hold", "factor", "high", false, true, ["reason"]),
  release_payment_hold: command("release_payment_hold", "release_carrier_payment_hold", "factor", "critical", false, true, ["holdId"]),
  create_invoice: command("create_invoice", "generate_customer_invoice", "customer", "high", false, true, ["invoiceId"]),
  create_settlement: command("create_settlement", "run_carrier_settlement_batch", "factor", "critical", false, true, ["settlementId"]),
};

export class TmsCommandRegistry {
  constructor(private readonly adapter: TmsCommandAdapter) {}

  get(commandName: TmsCommandName) {
    return TMS_COMMAND_DEFINITIONS[commandName];
  }

  list() {
    return Object.values(TMS_COMMAND_DEFINITIONS);
  }

  async execute(input: TmsCommandInput): Promise<TmsCommandExecutionResult> {
    const definition = this.get(input.commandName);
    const validationError = validateCommandInput(input, definition);

    // P0': clamp caller-supplied scores against the server-derived class floor so
    // the gates below can never read a model's optimistic self-report. The caller
    // may only move autonomy MORE restrictive (raise risk, lower confidence).
    const scores = clampAutonomyScores(
      { riskScore: input.riskScore, confidenceScore: input.confidenceScore },
      SERVER_AUTONOMY_FLOOR[definition.riskClass],
    );

    const permission = evaluatePolicy(input.policy ?? null, {
      tenantId: input.tenantId,
      loadId: input.loadId,
      carrierId: input.carrierId,
      customerId: input.customerId,
      actionType: definition.actionType,
      actorRole: "tma",
      audience: definition.audience,
      confidenceScore: scores.confidenceScore,
      riskScore: scores.riskScore,
      financialExposureCents: input.financialExposureCents ?? 0,
      customerSensitive: input.customerSensitive ?? false,
      ...input.extraPolicyFacts,
    });

    if (validationError) {
      return { commandName: definition.commandName, actionType: definition.actionType, audience: definition.audience, status: "blocked", permission, error: validationError };
    }

    // Non-bypassable guards run at this choke point, before policy and before any
    // provider call, and win over everything including bypass_permissions (T1).
    const guard: GuardResult = input.guardFacts
      ? evaluateNonBypassableGuards({
          ...input.guardFacts,
          command: {
            commandName: definition.commandName,
            audience: definition.audience,
            requiredCapability: CAPABILITY_BY_COMMAND[definition.commandName],
            isDispatchLike: DISPATCH_LIKE_COMMANDS.has(definition.commandName),
          },
        })
      : missingGuardFactsResult(definition);

    if (guard.decision === "block") {
      return {
        commandName: definition.commandName,
        actionType: definition.actionType,
        audience: definition.audience,
        status: "blocked",
        permission: withGuardReasons(permission, guard),
        error: guard.blocks.map((b) => b.reason).join("; "),
      };
    }

    if (permission.decision === "block") {
      return { commandName: definition.commandName, actionType: definition.actionType, audience: definition.audience, status: "blocked", permission, error: permission.reasons.join("; ") };
    }

    if (guard.decision === "approval_required") {
      return {
        commandName: definition.commandName,
        actionType: definition.actionType,
        audience: definition.audience,
        status: "approval_required",
        permission: withGuardReasons(permission, guard),
        error: guard.blocks.map((b) => b.reason).join("; "),
      };
    }

    if (!input.approvalOverride && permission.grantedPermissionLevel === "auto_mode" && !definition.canAutoExecute) {
      return {
        commandName: definition.commandName,
        actionType: definition.actionType,
        audience: definition.audience,
        status: "approval_required",
        permission: withApprovalReason(permission, "auto mode cannot execute this TMS command without approval"),
        error: "auto mode cannot execute this TMS command without approval",
      };
    }

    if (permission.grantedPermissionLevel === "bypass_permissions" && !definition.canBypass) {
      return {
        commandName: definition.commandName,
        actionType: definition.actionType,
        audience: definition.audience,
        status: "approval_required",
        permission: withApprovalReason(permission, "bypass permissions is not enabled for this TMS command"),
        error: "bypass permissions is not enabled for this TMS command",
      };
    }

    if (!input.approvalOverride && permission.decision === "approval_required") {
      return { commandName: definition.commandName, actionType: definition.actionType, audience: definition.audience, status: "approval_required", permission };
    }

    try {
      const providerResult = await this.adapter.executeCommand(input, definition);
      return { commandName: definition.commandName, actionType: definition.actionType, audience: definition.audience, status: "executed", permission, providerResult };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown TMS command error";
      return { commandName: definition.commandName, actionType: definition.actionType, audience: definition.audience, status: "failed", permission, error: message };
    }
  }
}

export class GenericTmsCommandSandboxAdapter implements TmsCommandAdapter {
  readonly provider = "generic_tms_sandbox";

  async executeCommand(input: TmsCommandInput, definition: TmsCommandDefinition): Promise<TmsCommandProviderResult> {
    return {
      provider: this.provider,
      externalId: `${this.provider}_${definition.commandName}_${stableIdPart(input.idempotencyKey)}`,
      status: "completed",
      rawProviderResponse: {
        commandName: definition.commandName,
        loadId: input.loadId,
        carrierId: input.carrierId,
        customerId: input.customerId,
        payload: input.payload,
        sandbox: true,
      },
      occurredAt: new Date().toISOString(),
    };
  }
}

function command(
  commandName: TmsCommandName,
  actionType: ActionType,
  audience: PermissionAudience,
  riskClass: TmsCommandDefinition["riskClass"],
  canAutoExecute: boolean,
  canBypass: boolean,
  requiredPayloadFields: string[],
): TmsCommandDefinition {
  return {
    commandName,
    actionType,
    audience,
    riskClass,
    canAutoExecute,
    canBypass,
    requiresIdempotencyKey: true,
    requiredPayloadFields,
    nonBypassableChecks: NON_BYPASSABLE_COMMON,
  };
}

function validateCommandInput(input: TmsCommandInput, definition: TmsCommandDefinition) {
  if (definition.requiresIdempotencyKey && !input.idempotencyKey.trim()) {
    return "TMS command requires an idempotency key";
  }
  const missingFields = definition.requiredPayloadFields.filter((field) => !(field in input.payload) && input[field as keyof TmsCommandInput] === undefined);
  if (missingFields.length > 0) {
    return `TMS command input missing required fields: ${missingFields.join(", ")}`;
  }
  return undefined;
}

function withGuardReasons(permission: PolicyEvaluationResult, guard: GuardResult): PolicyEvaluationResult {
  const reasons = guard.blocks.map((b) => b.reason);
  return {
    ...permission,
    decision: guard.decision === "block" ? "block" : "approval_required",
    grantedPermissionLevel: "ask_for_permission",
    grantedAutonomyMode: "ask_before_acting",
    reasons: [...new Set([...permission.reasons, ...reasons])],
    policyChecks: [
      ...permission.policyChecks,
      ...guard.blocks.map((b) => ({
        ruleId: `non_bypassable:${b.code}`,
        outcome: b.decision === "block" ? ("block" as const) : ("approval_required" as const),
        reason: b.reason,
      })),
    ],
  };
}

function withApprovalReason(permission: PolicyEvaluationResult, reason: string): PolicyEvaluationResult {
  return {
    ...permission,
    decision: "approval_required",
    grantedPermissionLevel: "ask_for_permission",
    grantedAutonomyMode: "ask_before_acting",
    reasons: [...new Set([...permission.reasons, reason])],
    policyChecks: [
      ...permission.policyChecks,
      { ruleId: "tms_command_guard", outcome: "approval_required", reason },
    ],
  };
}

function stableIdPart(value: string) {
  return value.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 80);
}
